<template>
    <p-modal name="salesByHourModal"
             label="Sales By Hour"
             ref="salesByHourModal"
             size="lg"
             @ok.prevent="handleModalOk">
        <template v-slot:default>
            <p-form ref="salesByHourForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Revenue and transactions by hour of day</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select data-type="posDevicesWithLocation"
                                                 rules="required"
                                                 :default-selected="true"
                                                 :initial-display-count="10"
                                                 select-all-button
                                                 v-model="reportParameters.registerIds" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Report By">
                                <b-row>
                                    <b-col>
                                        <b-form-radio :value="false" v-model="reportParameters.reportByCashier">POS Device</b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio :value="true" v-model="reportParameters.reportByCashier">Cashier</b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                            <p-card title="Additional Options">
                                <b-row>
                                    <b-col>
                                        <p-checkbox size="md"
                                                    label="Break Out By Day Of Week"
                                                    v-model="reportParameters.breakOutByDayOfWeek" />
                                    </b-col>
                                </b-row>
                            </p-card>
                            <p-card title="Format">
                                <b-row>
                                    <b-col>
                                        <b-form-radio value="pdf" v-model="outputFormat">PDF</b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio value="csv" v-model="outputFormat">CSV</b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate :user-report-template-options="userReportTemplateOptions"
                                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                                        :selected-user-report-template="selectedUserReportTemplate"
                                        v-model:template-name="templateName"
                                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary"
                              :is-busy="isBusy"
                              @click="cancel">Cancel</p-button>
                    <p-button variant="primary"
                              :is-busy="isBusy"
                              class="ms-2"
                              @click="ok">OK</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            outputFormat: 'pdf',
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Sales By Hour',
            reportParameters: {
                registerIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                reportByCashier: false,
                breakOutByDayOfWeek: false,
                locationId: null
            }
        };
    },
    methods: {
        resetForm() {
            this.outputFormat = 'pdf';
            this.reportParameters.dateOption = 0;
            this.reportParameters.startDate = new Date().toISOString();
            this.reportParameters.endDate = new Date().toISOString();
            this.reportParameters.reportByCashier = false;
            this.reportParameters.breakOutByDayOfWeek = false;
        },
        async show() {
            this.resetForm();

            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            this.reportParameters.registerIds = (posDevices || []).map(x => x.value);
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            await this.loadUserReportTemplates();

            this.$refs.salesByHourModal.show();
        },
        // ReportTemplates hook: POS devices saved into a template can be gone, or out of scope
        // for the current location, by the time it is loaded - so check them against the list the
        // picker itself offers rather than trusting the saved ids.
        async validateReportParameters(savedParameters) {
            if (!savedParameters.registerIds?.length) {
                return true;
            }

            const posDevices = await selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation');
            const availablePosDeviceIds = new Set((posDevices || []).map(posDevice => posDevice.value));

            return savedParameters.registerIds.every(registerId => availablePosDeviceIds.has(registerId));
        },
        async handleModalOk() {
            if (!(await this.$refs.salesByHourForm.validate())) {
                return;
            }

            if (this.reportParameters.registerIds.length === 0) {
                this.$appError('Please select one or more POS Devices.');
                return;
            }

            this.isBusy = true;

            try {
                if (!(await this.saveUserReportTemplate())) {
                    return;
                }

                const isPdf = this.outputFormat === 'pdf';

                const result = await axios.post(
                    isPdf ? 'sales/exportSalesByHour' : 'sales/exportSalesByHourCsv',
                    this.reportParameters,
                    { responseType: 'blob' });

                downloadFileToBrowser(
                    result.data,
                    `${this.reportName}.${isPdf ? 'pdf' : 'csv'}`,
                    isPdf ? 'application/pdf' : 'application/csv');

                this.$appSuccess(`${this.reportName} report generated successfully.`);
                this.$refs.salesByHourModal.hide();
            } finally {
                this.isBusy = false;
            }
        }
    }
};
</script>

<style scoped>
.static-height {
    height: 370px;
}
</style>
